/* eslint-disable complexity */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {arrayOf, bool, func, node, number, oneOf, shape, string, object} from 'prop-types';
import classNames from 'classnames';
import {propOr, T} from 'ramda';
import {JsonEditor} from 'jsoneditor-react';
import {appContextShape} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/AppContext';
import {Action} from '@sinclair-digital/sbg-storyline-components/src/components/Action';
import {default as Dialog} from '@sinclair-digital/sbg-storyline-components/src/components/Dialog';
import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/featureFlags';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import {default as Checkbox} from '@sinclair-digital/sbg-storyline-components/src/components/forms/Checkbox';
import {getSiteMapActions} from 'modules';
import classes from './SiteTreeRightPanel.scss';
import 'jsoneditor-react/es/editor.min.css';

const renderPersistedActions = ({
    isNewNode,
    hasRightPanelSubsection: {template},
    indexPresentation: {id},
}) => {
    if (isNewNode) {
        return null;
    }
    const actions = [];
    if (template) {
        actions.push(
            <div
                id="sda-change-template-link"
                key="sda-change-template-link"
                className="sda-setting-link"
                data-icon="fa-th-large"
                data-title="Presentation"
                data-panel-link-id="#sda-template-right-panel"
                data-info="no template selected"
                data-template-id=""
                data-presentation={id}
            />,
        );
    }
    actions.push(
        <div
            id="sda-seo-setting-link"
            key="sda-seo-setting-link"
            className="sda-setting-link"
            data-icon="fa-file-code-o"
            data-panel-link-id="#sda-seo-right-panel"
            data-title="SEO"
            data-info="included meta data"
        />,
    );
    return actions;
};

const isJsonValid = str => {
    try {
        JSON.parse(str);
        return str !== null;
    } catch (e) {
        return false;
    }
};

function EditJsonModal({value, onChange, onClose}) {
    const [jsonString, setJsonString] = useState(value);

    const handleDone = useCallback(() => {
        onChange(jsonString);
        onClose();
    }, [onChange, onClose, jsonString]);

    const handleJsonEditorChange = useCallback((json) => {
        try {
            setJsonString(JSON.stringify(json));
        } catch (e) {
            setJsonString('{}');
        }
    }, [setJsonString]);

    return (
        <Dialog isOpen className={classes.EditJsonModal} contentLabel="Edit JSON">
            <>
                <div className={classes.body}>
                    <JsonEditor
                        value={isJsonValid(jsonString) ? JSON.parse(jsonString) : {}}
                        onChange={handleJsonEditorChange}
                    />
                </div>
                <div className={classes.footer}>
                    <button
                        className="sda-btn-full sda-btn-alt"
                        type="button"
                        onClick={handleDone}
                    >
                        done
                    </button>
                    <button
                        className="sda-btn-full sda-btn-alt"
                        type="button"
                        onClick={onClose}
                    >
                        cancel
                    </button>
                </div>
            </>
        </Dialog>
    );
}

EditJsonModal.propTypes = {
    value: string.isRequired,
    onChange: func.isRequired,
    onClose: func.isRequired,
};


const SectionPagePanel = ({
    appContext,
    actions,
    adZone,
    className,
    data,
    isRightPanelFieldReadonly: {markup: readOnlyMarkup, URL},
    pathUrl,
    ...props
}) => {
    const [isEditJsonOpen, setIsEditJsonOpen] = useState(false);
    const [customJsonValue, setCustomJsonValue] = useState(null);
    const customJsonRef = useRef(null);

    const handleEditJsonClick = useCallback(e => {
        e.preventDefault();
        setIsEditJsonOpen(true);
    }, [setIsEditJsonOpen]);

    const handleEditJsonClose = useCallback(() => {
        setIsEditJsonOpen(false);
    }, [setIsEditJsonOpen]);

    const handleCustomJsonChange = useCallback(newValue => {
        setCustomJsonValue(newValue);
        customJsonRef.current.value = newValue;
    }, [setCustomJsonValue]);

    const handleCustomJsonTextareaChange = useCallback(e => {
        handleCustomJsonChange(e.target.value);
    }, [handleCustomJsonChange]);

    const injectedMarkup = propOr(null, 'injectedMarkup', data);

    useEffect(() => {
        if (getFeatureFlag('useCustomJson')) {
            const customJsonProp = JSON.stringify(propOr({}, 'customJson', data));

            if (!isJsonValid(customJsonProp)) {
                messageHandler('error', 'The CustomJson data we got back from the server was invalid');
            }

            setCustomJsonValue(customJsonProp);
        }
    }, [setCustomJsonValue, data]);

    return (
        <div className={classNames(classes.SectionPagePanel, className)} id="node-type-section">
            <div className="sda-input-group">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Page URL</label>
                    <div className="sda-error-wrapper">
                        <input
                            id="sda-node-url"
                            type="text"
                            className="sda-input-text-full"
                            defaultValue={pathUrl}
                            maxLength="256"
                            placeholder=""
                            readOnly={URL}
                        />
                    </div>
                </div>
            </div>
            <div className="sda-input-group bottom-padded">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Ad Zone</label>
                    <div className="sda-error-wrapper">
                        <input
                            id="sda-node-ad-zone"
                            type="text"
                            className="sda-input-text-full"
                            defaultValue={adZone}
                            maxLength="256"
                            placeholder=""
                        />
                    </div>
                </div>
            </div>
            <div className="sda-input-group bottom-padded">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Custom HTML</label>
                    <div className="sda-error-wrapper">
                        <textarea
                            className="sda-textarea-full"
                            id="sda-node-injected-markup"
                            placeholder=""
                            defaultValue={injectedMarkup}
                            readOnly={readOnlyMarkup}
                        />
                    </div>
                </div>
            </div>
            {appContext.getFeatureFlag('useCustomJson')
                ? (
                    <>
                        <div className="sda-input-group bottom-padded">
                            <div className="sda-input-group-inner">
                                <label className="sda-label">Custom JSON</label>
                                <div className="sda-error-wrapper">
                                    <input
                                        type="hidden"
                                        id="sda-node-custom-json"
                                        ref={customJsonRef}
                                        defaultValue={customJsonValue}
                                    />
                                    <textarea
                                        className={classNames(
                                            'sda-textarea-full',
                                            classes.customJsonTextbox
                                        )}
                                        placeholder=""
                                        value={customJsonValue}
                                        onChange={handleCustomJsonTextareaChange}
                                        readOnly={readOnlyMarkup}
                                    />
                                    <button
                                        className="sda-btn-full sda-btn-alt"
                                        type="button"
                                        onClick={handleEditJsonClick}
                                        disabled={readOnlyMarkup || !isJsonValid(customJsonValue)}
                                    >
                                        update custom json
                                    </button>
                                </div>
                            </div>
                        </div>
                        {isEditJsonOpen && (
                            <EditJsonModal
                                value={customJsonValue}
                                onChange={handleCustomJsonChange}
                                onClose={handleEditJsonClose}
                            />
                        )}
                    </>
                )
                : null
            }
            {renderPersistedActions(props)}
            {actions}
        </div>
    );
};

SectionPagePanel.propTypes = {
    className: string,
    isNewNode: bool,
    parentSite: shape({
        siteSlug: string,
    }),
    indexPresentation: shape({
        id: string,
    }),
    pathUrl: string,
    adZone: string,
    data: shape({
        injectedMarkup: string,
        customJson: object,
    }),
    actions: arrayOf(node),
    hasRightPanelSubsection: shape({
        template: bool.isRequired,
        linkedPackage: bool.isRequired,
    }).isRequired,
    isRightPanelFieldReadonly: shape({
        URL: bool,
        markup: bool,
    }).isRequired,
};

const contentURLOptions = [
    {value: '/story', category: 'content-story', disablable: false},
    {value: '/gallery', category: 'content-story-gallery', disablable: true},
    {value: '/bio', category: 'content-biography', disablable: true},
    {value: '/more', category: 'content-more', disablable: true},
];

const ContentPagePanel = ({
    appContext,
    actions,
    adZone,
    className,
    hasRightPanelSubsection: {template: hasTemplate},
    isRightPanelFieldReadonly: {URL: urlFieldReadOnly},
    isRootNode,
    pathUrl,
    presentationCategoryType,
    presentationUuid,
    rootPresentation,
    customHtml, // same as "data" from SectionPagePanel
}) => {
    const injectedMarkup = propOr(null, 'injectedMarkup', customHtml);
    // TODO(dmauldin): use a try/catch here to avoid stringifying non-JSON values or at least recover gracefully and log the error
    const customJsonProp = propOr(null, 'customJson', customHtml);
    let customJson;
    if (getFeatureFlag('useCustomJson')) {
        try {
            customJson = customJsonProp === null ? null : JSON.stringify(customJsonProp);
        } catch {
            customJson = '';
            messageHandler('error', 'The CustomJson data we got back from the server was invalid');
        }
    }
    const classList = classNames(classes.ContentPagePanel, className);
    let rootOptions = null;
    if (isRootNode) {
        const urlReadOnly = (rootPresentation && rootPresentation.length > 0) || urlFieldReadOnly;

        rootOptions =
            presentationCategoryType !== 'Index' ? (
                <div className="sda-input-group">
                    <div className="sda-input-group-inner">
                        <label className="sda-label">Page URL</label>
                        <div className="sda-error-wrapper">
                            <input
                                id="sda-presentation-url"
                                type="text"
                                className="sda-input-text-full"
                                defaultValue={pathUrl}
                                readOnly={urlReadOnly}
                                maxLength="256"
                                placeholder=""
                            />
                        </div>
                    </div>
                </div>
            ) : null;
    } else {
        const urlReadOnly = urlFieldReadOnly;
        rootOptions = (
            <div className="sda-input-group">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Page URL</label>
                    <div className="sda-select-firefox fa-angle-down">
                        <select
                            id="sda-node-url-select"
                            className="sda-select-element"
                            defaultValue={contentURLOptions[0].value}>
                            {contentURLOptions.map(opt => (
                                <option
                                    disabled={opt.disablable && urlReadOnly}
                                    value={opt.value}
                                    key={opt.value}
                                    data-presentation-category={opt.category}>
                                    {opt.value}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classList} id="node-type-presentation">
            {rootOptions}

            <div className="sda-input-group bottom-padded">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Ad Zone</label>
                    <div className="sda-error-wrapper">
                        <input
                            id="sda-node-ad-zone"
                            type="text"
                            className="sda-input-text-full"
                            defaultValue={adZone}
                            maxLength="256"
                            placeholder=""
                        />
                    </div>
                </div>
            </div>
            {appContext.getFeatureFlag('StoryOverrideCustomHTML')
                ? (<div className="sda-input-group bottom-padded">
                    <div className="sda-input-group-inner">
                        <label className="sda-label">Custom HTML</label>
                        <div className="sda-error-wrapper">
                            <textarea
                                className="sda-textarea-full"
                                id="sda-node-injected-markup"
                                placeholder=""
                                defaultValue={injectedMarkup}
                            />
                        </div>
                    </div>
                </div>)
                : null
            }
            {appContext.getFeatureFlag('useCustomJson')
                ? (<div className="sda-input-group bottom-padded">
                    <div className="sda-input-group-inner">
                        <label className="sda-label">Custom JSON</label>
                        <div className="sda-error-wrapper">
                            <textarea
                                className="sda-textarea-full"
                                id="sda-node-custom-json"
                                placeholder=""
                                defaultValue={customJson}
                            />
                        </div>
                    </div>
                </div>)
                : null
            }
            {hasTemplate ? (
                <div
                    id="sda-change-template-link"
                    className="sda-setting-link"
                    data-icon="fa-th-large"
                    data-title="Presentation"
                    data-panel-link-id="#sda-template-right-panel"
                    data-info="no template selected"
                    data-template-id=""
                    data-presentation={presentationUuid}
                />
            ) : null}

            {actions}
        </div>
    );
};

ContentPagePanel.propTypes = {
    appContext: shape(appContextShape).isRequired,
    className: string,
    isRootNode: bool,
    rootPresentation: shape({
        length: number,
    }),
    presentationCategoryType: string,
    pathUrl: string,
    adZone: string,
    parentUuid: string,
    presentationReference: shape({
        siteSlug: string,
    }),
    uuid: string,
    presentationUuid: string,
    actions: arrayOf(node),
    hasRightPanelSubsection: shape({
        template: bool.isRequired,
    }).isRequired,
    isRightPanelFieldReadonly: shape({
        URL: bool,
    }).isRequired,
    customHtml: shape({
        injectedMarkup: string,
        customJson: string,
    }),
};

const TopicalPagePanel = ({
    actions,
    adZone,
    className,
    hasRightPanelSubsection: {template: hasTemplate},
    isRightPanelFieldReadonly: {URL: urlReadOnly},
    parentUuid,
    pathUrl,
}) => {
    const classList = classNames(classes.TopicalPagePanel, className);

    return (
        <div className={classList} id="node-type-topical">
            <div className="sda-input-group">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Page URL</label>
                    <div className="sda-error-wrapper">
                        <input
                            id="sda-node-url"
                            type="text"
                            className="sda-input-text-full"
                            defaultValue={pathUrl}
                            maxLength="256"
                            placeholder=""
                            readOnly={urlReadOnly}
                        />
                    </div>
                </div>
            </div>
            <div className="sda-input-group bottom-padded">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Ad Zone</label>
                    <div className="sda-error-wrapper">
                        <input
                            id="sda-node-ad-zone"
                            type="text"
                            className="sda-input-text-full"
                            defaultValue={adZone}
                            maxLength="256"
                            placeholder=""
                        />
                    </div>
                </div>
            </div>
            {hasTemplate ? (
                <div
                    id="sda-change-template-link"
                    className="sda-setting-link"
                    data-icon="fa-th-large"
                    data-title="Presentation"
                    data-panel-link-id="#sda-template-right-panel"
                    data-info="no template selected"
                    data-template-id=""
                    data-presentation={parentUuid}
                />
            ) : null}
            {actions}
        </div>
    );
};

TopicalPagePanel.propTypes = {
    className: string,
    isNewNode: bool,
    uuid: string,
    pathUrl: string,
    adZone: string,
    parentUuid: string,
    actions: arrayOf(node),
    hasRightPanelSubsection: shape({
        template: bool.isRequired,
    }).isRequired,
    isRightPanelFieldReadonly: shape({
        URL: bool,
    }).isRequired,
};

const AliasPagePanel = ({
    className,
    isRightPanelFieldReadonly: {redirectURL: redirectReadOnly, URL: urlReadOnly},
    pathUrl,
}) => {
    const classList = classNames(classes.AliasPagePanel, className, {});

    return (
        <div className={classList} id="node-type-alias">
            <div className="sda-input-group">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Page URL</label>
                    <div className="sda-error-wrapper">
                        <input
                            id="sda-node-url"
                            type="text"
                            className="sda-input-text-full"
                            defaultValue={pathUrl}
                            maxLength="256"
                            placeholder=""
                            readOnly={urlReadOnly}
                        />
                    </div>
                </div>
            </div>
            <div className="sda-input-group">
                <div className="sda-input-group-inner">
                    <label className="sda-label">Redirect URL</label>
                    <div className="sda-error-wrapper">
                        <input
                            id="sda-node-redirect-url"
                            type="text"
                            className="sda-input-text-full"
                            defaultValue=""
                            maxLength="256"
                            placeholder=""
                            readOnly={redirectReadOnly}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

AliasPagePanel.propTypes = {
    className: string,
    pathUrl: string,
    isRightPanelFieldReadonly: shape({
        URL: bool,
        redirectURL: bool,
    }).isRequired,
};

const panelTypes = {
    alias: AliasPagePanel,
    topical: TopicalPagePanel,
    presentation: ContentPagePanel,
    section: SectionPagePanel,
};

const typeToPanelClass = type => {
    return panelTypes[type] || panelTypes.section;
};

// Disabling since latest eslint can't handle prop-type composition
/* eslint react/prop-types: 1 */
export const SiteTreeRightTypesPanel = ({className, appContext, type, ...props}) => {
    const classList = classNames(classes.SiteTreeRightPanel, className, 'sda-page-type-group', 'reacted');

    const PanelType = typeToPanelClass(type);

    const actions = getSiteMapActions()
        .filter(({checkVisible = T}) => checkVisible(appContext))
        .map(({text, href, ...rest}) => (
            <Action
                key={href}
                {...{
                    label: text,
                    target: href,
                    siteSlug: props.siteSlug,
                    uuid: props.uuid,
                    ...rest,
                }}
            />
        ));

    return <PanelType {...props} appContext={appContext} className={classList} actions={actions} />;
};

SiteTreeRightTypesPanel.propTypes = {
    ...SectionPagePanel.propTypes,
    ...ContentPagePanel.propTypes,
    ...TopicalPagePanel.propTypes,
    ...AliasPagePanel.propTypes,

    className: string,

    type: oneOf(Object.keys(panelTypes)).isRequired,
    siteSlug: string,
    uuid: string,

    appContext: shape(appContextShape).isRequired,

    hasRightPanelSubsection: shape({
        template: bool.isRequired,
        linkedPackage: bool.isRequired,
    }).isRequired,
    isRightPanelFieldReadonly: shape({
        name: bool,
        URL: bool,
        redirectURL: bool,
        markup: bool,
    }).isRequired,
};

const lowerCaseOrDefault = (value, def) => (value ? value.toLowerCase() : def);

const getNodeType = type => {
    switch (type) {
        case 'section':
            return 'Section Page';
        case 'presentation':
            return 'Content Page';
        case 'Topical':
            return 'Topical Page';
        case 'Alias':
            return 'Alias';
    }

    return 'Error';
};

export class SiteTreeRightPanel extends React.Component {
    static propTypes = {
        appContext: shape(appContextShape).isRequired,
        className: string,
        presentationCategoryType: string,
        status: string,
        type: string,
        isNewNode: bool,
        rootPresentation: shape({
            length: number,
        }),
        parentUuid: string,
        templateId: string,
        presentationUuid: string,
        uuid: string,
        indexPresentation: shape({
            uuid: string,
        }).isRequired,
        name: string,
        siteSlug: string,
        isLoading: bool,
        targetable: bool,

        ...SiteTreeRightTypesPanel.propTypes,
    };

    /**
     * <Checkbox/> is a controlled form component and needs a state
     * By default, the section is targetable unless specified otherwise
     */
    state = {
        targetable: true,
    }

    componentDidUpdate(prevProps) {
        const isMountingNewSectionData = !Object.hasOwn(prevProps, 'targetable') && Object.hasOwn(this.props, 'targetable');
        if (isMountingNewSectionData) {
            this.setState({targetable: this.props.targetable});
        }
    }

    renderNode() {
        const {
            className,
            presentationCategoryType,
            status,
            type,
            name,
            isNewNode,
            rootPresentation,
            appContext,
            isRightPanelFieldReadonly,
            parentUuid,
            uuid,
            templateId,
            indexPresentation,
        } = this.props;
        const classList = classNames(classes.SiteTreeRightPanel, 'reacted', className, {
            'panel-open': true,
        });

        const content = lowerCaseOrDefault(presentationCategoryType, 'contentstory');

        const pageType = isNewNode ? (
            <div className="sda-select-firefox fa-angle-down">
                <select id="sda-node-type" className="sda-select-element">
                    <option value="section">Section Page</option>
                    <option value="presentation">Content Page</option>
                </select>
            </div>
        ) : (
            <input className="sda-input-text-full" type="text" value={getNodeType(type)} readOnly />
        );

        const nodeIsReadOnly = rootPresentation && rootPresentation.length > 0;

        const cancelButton = (
            <a id="sda-node-cancel-btn" className="sda-btn-alt">
                cancel
            </a>
        );
        let saveCancelButtonRow;
        if (appContext.getFeatureFlag('sectionSave')) {
            saveCancelButtonRow = (
                <ul className="sda-btns-horizontal">
                    <li>
                        <a id="sda-node-save-btn" className="sda-btn-alt">
                            save
                        </a>
                    </li>
                    <li>{cancelButton}</li>
                </ul>
            );
        } else {
            saveCancelButtonRow = cancelButton;
        }

        return (
            <div className={classList}>
                <header
                    className="panel-header align-right"
                    data-node-type={type}
                    data-new-node={isNewNode}
                    data-parent-uuid={parentUuid}
                    data-template-id={templateId}
                    data-node-uuid={uuid}
                    data-content-type={content}
                    data-presentation-uuid={indexPresentation.uuid}>
                    <sinclair-status id="sda-node-status" data-status={lowerCaseOrDefault(status, 'draft')} />
                </header>
                <main className="panel-main">
                    <header className="panel-title">Page Settings</header>
                    <section className="panel-section">
                        <main className="panel-section-body">
                            {getFeatureFlag('nonTargetableSection:enabled') &&
                                <Checkbox
                                    id="sda-node-targetable"
                                    className={classes.targetableCheckbox}
                                    theme="toggle"
                                    label="Targetable?"
                                    labelPosition="left"
                                    checked={this.state.targetable}
                                    onChange={() => this.setState({targetable: !this.state.targetable})}
                                />
                            }
                            <div className="sda-input-group">
                                <div className="sda-input-group-inner">
                                    <label className="sda-label">Page Type</label>
                                    {pageType}
                                </div>
                            </div>
                            <div className="sda-input-group">
                                <div className="sda-input-group-inner">
                                    <label className="sda-label">Page Name</label>
                                    <div className="sda-error-wrapper">
                                        <input
                                            id="sda-node-name"
                                            type="text"
                                            className="sda-input-text-full"
                                            defaultValue={name}
                                            readOnly={nodeIsReadOnly || isRightPanelFieldReadonly.name}
                                            maxLength="256"
                                            placeholder="enter name here..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div id="sda-node-right-panel-types">
                                <SiteTreeRightTypesPanel {...this.props} />
                            </div>
                        </main>
                    </section>
                </main>
                <footer className="panel-footer">
                    {saveCancelButtonRow}
                    <a id="sda-node-publish-btn" className="sda-btn-full sda-btn-red">
                        publish page
                    </a>
                </footer>
            </div>
        );
    }

    render() {
        return this.props.isLoading ? (
            <div id="sda-node-panel-loader" className="sda-access-process-loader" />
        ) : (
            this.renderNode()
        );
    }
}

export default SiteTreeRightPanel;
